import type { EpicParityView, ParityRunSummary, AcceptDifferenceRequest } from '../../../../shared/types/parity';
import { authFetch } from './authFetch';
import { API_BASE } from './version';

/** FORGE-348 — Parity Proof API client (see backend/src/routes/parity.ts). */

async function errorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { error?: { message?: string } };
    if (body?.error?.message) return body.error.message;
  } catch { /* not JSON */ }
  return fallback;
}

export async function fetchEpicParity(epicId: string): Promise<EpicParityView> {
  const response = await authFetch(`${API_BASE}/epics/${encodeURIComponent(epicId)}/parity`, { method: 'GET' });
  if (!response.ok) throw new Error(await errorMessage(response, `Failed to load parity: ${response.statusText}`));
  return ((await response.json()) as { data: EpicParityView }).data;
}

export async function fetchProjectParityRuns(projectId: string): Promise<ParityRunSummary[]> {
  const response = await authFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/parity-runs`, { method: 'GET' });
  if (!response.ok) throw new Error(await errorMessage(response, `Failed to load parity reports: ${response.statusText}`));
  return ((await response.json()) as { data: ParityRunSummary[] }).data;
}

export async function approveParityUnderstanding(runId: string, repo: string): Promise<void> {
  const response = await authFetch(`${API_BASE}/parity/${encodeURIComponent(runId)}/approve`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ repo }),
  });
  if (!response.ok) throw new Error(await errorMessage(response, `Approval failed: ${response.statusText}`));
}

export async function acceptParityDifference(runId: string, req: AcceptDifferenceRequest): Promise<void> {
  const response = await authFetch(`${API_BASE}/parity/${encodeURIComponent(runId)}/accept`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(req),
  });
  if (!response.ok) throw new Error(await errorMessage(response, `Could not record the accepted difference: ${response.statusText}`));
}
